import { Fragment, ReactNode } from "react";

/**
 * Turns stored HTML (if any) into plain text: block ends become newlines
 * and every other tag is dropped. Rendering then builds React elements, so
 * nothing from the description is ever injected as markup.
 */
export function toPlainText(value: string): string {
  return value
    .replace(/<(script|style)\b[^>]*>[\s\S]*?<\/\1\s*>/gi, "")
    .replace(/<\s*br\s*\/?>/gi, "\n")
    .replace(/<\/\s*(p|div|li|h[1-6])\s*>/gi, "\n")
    .replace(/<li[^>]*>/gi, "- ")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\r\n?/g, "\n");
}

/** **bold** spans; everything else is literal text. */
function inline(text: string): ReactNode[] {
  return text.split(/(\*\*[^*]+\*\*)/g).map((part, index) =>
    /^\*\*[^*]+\*\*$/.test(part) ? (
      <strong key={index}>{part.slice(2, -2)}</strong>
    ) : (
      <Fragment key={index}>{part}</Fragment>
    ),
  );
}

const BULLET = /^\s*[-*•]\s+/;
const NUMBERED = /^\s*\d+[.)]\s+/;
const HEADING = /^\s*#{1,3}\s+/;

/**
 * A job description: paragraphs and line breaks kept, plus the bits of
 * markdown recruiters tend to type (- bullets, 1. lists, # headings, **bold**).
 */
export const Description = ({ text }: { text?: string | null }) => {
  const source = toPlainText(String(text ?? "")).trim();
  if (!source) {
    return <p className="text-gray-500">No description provided.</p>;
  }

  const blocks = source.split(/\n\s*\n/);

  return (
    <div className="flex flex-col gap-4 text-base leading-7 text-gray-800">
      {blocks.map((block, blockIndex) => {
        const lines = block.split("\n").filter((line) => line.trim());

        if (lines.every((line) => BULLET.test(line))) {
          return (
            <ul key={blockIndex} className="list-disc space-y-1 pl-6">
              {lines.map((line, i) => (
                <li key={i}>{inline(line.replace(BULLET, ""))}</li>
              ))}
            </ul>
          );
        }
        if (lines.every((line) => NUMBERED.test(line))) {
          return (
            <ol key={blockIndex} className="list-decimal space-y-1 pl-6">
              {lines.map((line, i) => (
                <li key={i}>{inline(line.replace(NUMBERED, ""))}</li>
              ))}
            </ol>
          );
        }

        return (
          <Fragment key={blockIndex}>
            {lines.map((line, i) =>
              HEADING.test(line) ? (
                <h3 key={i} className="text-lg font-semibold text-gray-900">
                  {inline(line.replace(HEADING, ""))}
                </h3>
              ) : null,
            )}
            {lines.some((line) => !HEADING.test(line)) && (
              <p>
                {lines
                  .filter((line) => !HEADING.test(line))
                  .map((line, i, all) => (
                    <Fragment key={i}>
                      {inline(line)}
                      {i < all.length - 1 && <br />}
                    </Fragment>
                  ))}
              </p>
            )}
          </Fragment>
        );
      })}
    </div>
  );
};

export default Description;
